import React, { Dispatch, useContext, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import { debounce } from '@mui/material/utils'
import InfoIcon from '@mui/icons-material/Info'
import { IssueEntity } from 'types/IssueEntity'
import { simplifyFraction } from '../utils'
import * as sharesOrUnits from '../../../utils/issues/sharesOrUnits.js'
import { FactsTableRow } from '../../components/FactsTable'
import { PlainNumericField, PlainNumericFieldProps } from '../../../components/fields/NumericField'
import { formatMoney, formatNumber } from '../../../packages/utils/numberFormatters'
import { DynamicIconButton } from '../../../utils/tooltip/DynamicIconButton'
import { UserMe } from '../../../api/UserApi'
import { SetStateAction } from 'jotai'
import { UserMeContext } from '../../../contexts/UserMeContext'

const Wrapper = ({
    children,
    hasBottomPadding,
    hasDivider = false,
    isOpen,
    onToggle,
    t,
}: { hasBottomPadding?: boolean; hasDivider?: boolean; t: (key: string, options?: Record<string, unknown>) => string } & (
    | {
          children: React.ReactNode
          isOpen: boolean
          onToggle: (isOpen: boolean) => void
      }
    | { children: React.ReactNode; isOpen?: undefined; onToggle?: undefined }
)) => (
    <FactsTableRow
        hasBottomPadding={hasBottomPadding}
        hasDivider={hasDivider}
        label={
            <>
                {t('pages.issues.terms.conditions')} <br />
                {onToggle && (
                    <Button onClick={() => onToggle(!isOpen)} sx={{ padding: 0 }}>
                        {t('pages.issues.terms.showRightsCalculator', {
                            action: isOpen ? t('pages.issues.terms.hide') : t('pages.issues.terms.show'),
                        })}
                    </Button>
                )}
            </>
        }
    >
        {children}
    </FactsTableRow>
)

const RightsCalculator = ({
    currency,
    rightsRequired,
    unitSubPrice,
    unitSubPriceSek,
    unitsReceived,
}: {
    currency: NonNullable<IssueEntity['currency']>
    rightsRequired: NonNullable<IssueEntity['rightsRequired']>
    unitSubPrice: NonNullable<IssueEntity['unitSubPrice']>
    unitSubPriceSek: NonNullable<IssueEntity['unitSubPriceSek']>
    unitsReceived: NonNullable<IssueEntity['unitsReceived']>
}) => {
    const { t, i18n } = useTranslation()
    const userMeContext = useContext(UserMeContext)
    const { settings } = userMeContext.user || { settings: null }
    const locale = i18n.language
    const [amountValue, setAmountValue] = React.useState(settings?.rightsCalculator.amount ?? 100000)
    const [percentageValue, setPercentageValue] = React.useState(settings?.rightsCalculator.percentage ?? 3)

    const rightsCount = Math.ceil(((amountValue / +unitSubPriceSek) * rightsRequired) / unitsReceived)
    const sekAmount = (((percentageValue / 100) * +unitSubPrice) / rightsRequired) * unitsReceived

    const debouncedUpdateMe = useMemo(
        () =>
            debounce(async (field: string, value: number) => {
                await userMeContext.updateMe({
                    settings: {
                        ...settings,
                        rightsCalculator: {
                            amount: null,
                            percentage: null,
                            ...settings?.rightsCalculator,
                            [field]: value,
                        },
                    },
                })
            }, 500),
        [userMeContext, settings],
    )

    const handleChangeSetting = (
        field: keyof UserMe['settings']['rightsCalculator'],
        value: number,
        setState: Dispatch<SetStateAction<number>>,
    ) => {
        setState(value)

        void debouncedUpdateMe(field, value)
    }

    const numericFieldProps: Partial<PlainNumericFieldProps> = {
        sx: {
            marginTop: '-1px',
        },
        slotProps: {
            htmlInput: {
                sx: {
                    padding: 0,
                },
            },
        },
        variant: 'standard',
    }

    return (
        <>
            <FactsTableRow
                hasBottomPadding={false}
                hasDivider={false}
                label={
                    <>
                        {t('pages.issues.terms.subscribeAmount')}{' '}
                        <PlainNumericField
                            {...numericFieldProps}
                            autoFocus={!settings?.rightsCalculator.amount}
                            onChange={(val) => handleChangeSetting('amount', +val, setAmountValue)}
                            sx={{
                                ...numericFieldProps.sx,
                                width:
                                    amountValue < 100000
                                        ? 45
                                        : amountValue < 1000000
                                          ? 55
                                          : amountValue < 1000000
                                            ? 65
                                            : 75,
                                '& input': {
                                    textAlign: 'center',
                                },
                            }}
                            value={amountValue.toString()}
                        />{' '}
                        SEK
                    </>
                }
            >
                {'=>'} {formatNumber(rightsCount, { decimals: 0, locale }) ?? '-'} {t('pages.issues.terms.rightsWord')}
            </FactsTableRow>

            <FactsTableRow
                label={
                    <>
                        <div style={{ display: 'inline-flex' }}>
                            <div style={{ paddingRight: 5 }}>{t('pages.issues.terms.payPremium')} </div>
                            <PlainNumericField
                                {...numericFieldProps}
                                sx={{
                                    ...numericFieldProps.sx,
                                    width: 35,
                                    '& input': {
                                        textAlign: 'center',
                                    },
                                }}
                                percentage
                                onChange={(val) => handleChangeSetting('percentage', +val, setPercentageValue)}
                                value={percentageValue.toString()}
                            />
                        </div>
                        <DynamicIconButton
                            title={t('pages.issues.terms.premiumTooltip')}
                            IconComponent={InfoIcon}
                            placement='right'
                            padding='0 0 0 10px'
                        />
                    </>
                }
            >
                {'=>'} {formatMoney(sekAmount, { currencySymbol: currency, locale, minDecimals: 2, maxDecimals: 5 })}{' '}
                {t('pages.issues.terms.perRight')}
            </FactsTableRow>
        </>
    )
}

export function TermsRow({ issue }: { issue: Partial<IssueEntity> }) {
    const [isOpen, setIsOpen] = React.useState(false)
    const { t, i18n } = useTranslation()
    const locale = i18n.language

    const {
        currency,
        sharesRequired,
        rightsReceived,
        rightsRequired,
        unitsReceived,
        sharesInUnit,
        unitSubPrice,
        unitSubPriceSek,
    } = issue

    const notDetermined = !sharesRequired || !rightsReceived || !rightsRequired || !unitsReceived

    if (notDetermined) {
        return (
            <Wrapper t={t}>
                <span>{t('pages.issues.terms.notDeterminedYet')}</span>
            </Wrapper>
        )
    }

    const handleToggle = (_isOpen: boolean) => {
        setIsOpen(_isOpen)
    }

    const { valueOne: newShares, valueTwo: existingShares } = simplifyFraction(
        (sharesInUnit || 0) * unitsReceived * rightsReceived,
        sharesRequired * rightsRequired,
    )

    const termsExplanation = t('pages.issues.terms.termsExplanation', {
        newShares,
        newSharesLabel: newShares > 1 ? t('pages.issues.terms.newShares') : t('pages.issues.terms.newShare'),
        existingShares,
        existingSharesLabel:
            existingShares > 1 ? t('pages.issues.terms.existingShares') : t('pages.issues.terms.existingShare'),
        ratio: `${newShares}:${existingShares}`,
    })
    const hasRightsCalculator =
        issue.type === 'Rights issue' &&
        typeof unitSubPrice === 'string' &&
        typeof unitSubPriceSek === 'string' &&
        typeof currency === 'string'

    return (
        <>
            <Wrapper hasBottomPadding={!hasRightsCalculator} hasDivider={!hasRightsCalculator} t={t}>
                {sharesInUnit !== 0 ? <Typography>{termsExplanation}</Typography> : null}
                {sharesOrUnits.sharesQuantityLabel(sharesRequired, locale)} ={'>'}{' '}
                {sharesOrUnits.rightsQuantityLabel(rightsReceived, locale)} <br />
                {sharesOrUnits.rightsQuantityLabel(rightsRequired, locale)} ={'>'}{' '}
                {sharesOrUnits.quantityLabel(issue, unitsReceived, locale)}
            </Wrapper>
            {hasRightsCalculator && (
                <>
                    <FactsTableRow
                        hasBottomPadding={!isOpen}
                        hasDivider={!isOpen}
                        label={
                            <>
                                <Button onClick={() => handleToggle(!isOpen)} sx={{ padding: 0 }}>
                                    {t('pages.issues.terms.showRightsCalculator', {
                                        action: isOpen ? t('pages.issues.terms.hide') : t('pages.issues.terms.show'),
                                    })}
                                </Button>
                            </>
                        }
                    ></FactsTableRow>
                    {isOpen && (
                        <RightsCalculator
                            currency={currency}
                            rightsRequired={rightsRequired}
                            unitSubPrice={unitSubPrice}
                            unitSubPriceSek={unitSubPriceSek}
                            unitsReceived={unitsReceived}
                        />
                    )}
                </>
            )}
        </>
    )
}
